import React from 'react';
import {
    Illustration,
    InteractiveImg,
    EmailQuizGeneral,
    Message,
    Password,
    Question,
    CustomPresentation,
    RadioButtons,
    CheckBoxes,
    NumericalScale,
    OrderableList,
    YesNoList,
    Questionwithbuttons,
    Questionwithimage,
    Questionwithsocialpost,
    PasswordTester,
    Conversation,
    GroupOfBlocks,
} from '../questions';
import { TextDefault } from './ui';

const noop = () => {};

/**
 * Ensure list-shaped `ui` fields exist so runtime widgets do not throw on
 * `.map` during canvas / admin preview (definitions may omit empty arrays).
 */
function normalizeBlockForRuntimePreview(block) {
    if (!block) return null;
    const b = { ...block };
    if (!Array.isArray(b.options)) b.options = [];
    if (!Array.isArray(b.questions)) b.questions = [];
    if (!Array.isArray(b.items)) b.items = [];
    if (!Array.isArray(b.buttons)) b.buttons = [];
    const groupSrc = b.group || b.blocks;
    if (!Array.isArray(b.group) && Array.isArray(groupSrc)) {
        b.group = groupSrc;
    }
    if (!Array.isArray(b.group)) b.group = [];
    return b;
}

function normalizePresentation(presentation) {
    if (!presentation) return null;
    const p = { ...presentation };
    if (p.image && !p.imageUrl) {
        p.imageUrl = p.image;
    }
    // Visual editor stores a single URL as imageUrl; presentation components
    // historically read `image`.
    if (
        !Array.isArray(p.image) &&
        (p.image == null || p.image === '') &&
        p.imageUrl
    ) {
        p.image = p.imageUrl;
    }
    if (!Array.isArray(p.hoverPoints)) {
        p.hoverPoints = [];
    }
    return p;
}

/**
 * Same presentation mapping as assessment {@link Question} (non-review).
 */
export function RuntimePresentationView({
    presentation,
    userName = '',
    userEmail = '',
}) {
    const p = normalizePresentation(presentation);
    if (!p) {
        return (
            <TextDefault lighter>Add a presentation to see it here.</TextDefault>
        );
    }
    switch (p.type) {
        case 'illustration':
            return <Illustration ui={p} />;
        case 'interactive-img':
            return <InteractiveImg ui={p} />;
        case 'email': {
            const emailUi = {
                ...p,
                email: p.email || { showInstructionsOverlay: false },
            };
            return (
                <EmailQuizGeneral
                    ui={emailUi}
                    onAction={noop}
                    userEmail={userEmail}
                    userName={userName}
                    isInAnswerMode={false}
                />
            );
        }
        case 'custom':
            return <CustomPresentation ui={p} onAction={noop} />;
        case 'email-mdlz-newsletter':
            return (
                <TextDefault lighter>
                    email-mdlz-newsletter — use preview for full layout
                </TextDefault>
            );
        case 'message':
            return <Message ui={p} onAction={noop} />;
        case 'password':
            return <Password ui={p} onAction={noop} />;
        case 'question':
            return <Question ui={p} />;
        case 'video':
            return (
                <TextDefault lighter>
                    Video step — shown in learner preview when configured.
                </TextDefault>
            );
        default:
            return (
                <TextDefault lighter>
                    Unknown presentation type: {p.type}
                </TextDefault>
            );
    }
}

/**
 * Same interactive widgets as assessment flow; values are empty (layout preview).
 */
export function RuntimeInteractiveBlockView({ block }) {
    if (!block) return null;
    const ui = normalizeBlockForRuntimePreview(block);
    switch (ui.type) {
        case 'radio':
            return (
                <RadioButtons ui={ui} handleChange={noop} value={undefined} />
            );
        case 'checkbox':
            return (
                <CheckBoxes ui={ui} handleChange={noop} value={undefined} />
            );
        case 'numericalscale':
            return (
                <NumericalScale ui={ui} handleChange={noop} value={undefined} />
            );
        case 'orderablelist':
            return (
                <OrderableList ui={ui} handleChange={noop} value={undefined} />
            );
        case 'yes-no-list':
            return (
                <YesNoList ui={ui} handleChange={noop} value={{}} />
            );
        case 'questionwithbuttons':
            return (
                <Questionwithbuttons
                    ui={ui}
                    handleChange={noop}
                    value={undefined}
                />
            );
        case 'questionwithimage':
            return (
                <Questionwithimage
                    ui={ui}
                    handleChange={noop}
                    value={undefined}
                />
            );
        case 'questionwithsocialpost':
            return (
                <Questionwithsocialpost
                    ui={ui}
                    handleChange={noop}
                    value={undefined}
                />
            );
        case 'password-tester':
            return (
                <PasswordTester ui={ui} handleChange={noop} value={undefined} />
            );
        case 'conversation':
            return (
                <Conversation ui={ui} handleChange={noop} value={[]} />
            );
        case 'groupOfBlocks':
            return (
                <GroupOfBlocks ui={ui} handleChange={noop} value={undefined} />
            );
        case 'decision':
            return (
                <TextDefault lighter>
                    Decision / video block — preview playback in scenario
                    preview.
                </TextDefault>
            );
        case 'blockHidden':
            return (
                <TextDefault lighter>
                    Hidden block — learner sees presentation only; scores from
                    linked actions.
                </TextDefault>
            );
        default:
            return (
                <TextDefault lighter>Unknown block: {ui.type}</TextDefault>
            );
    }
}
